import React, { useState } from 'react';
import { 
  Typography, 
  Box,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  Divider,
  Paper,
  Alert,
  Snackbar,
  MenuItem,
  Stack
} from '@mui/material';
import PageTemplate from '../components/PageTemplate';
import {
  Email as EmailIcon,
  Help as HelpIcon,
  BusinessCenter as BusinessIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';

const ContactPage: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    reason: 'general'
  });
  
  const [formErrors, setFormErrors] = useState<{
    name?: string;
    email?: string;
    subject?: string;
    message?: string;
  }>({});
  
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when field is edited
    if (formErrors[name as keyof typeof formErrors]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };
  
  const validateForm = () => {
    const errors: {
      name?: string;
      email?: string;
      subject?: string;
      message?: string;
    } = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    }
    
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email)) {
      errors.email = 'Invalid email address';
    }
    
    if (!formData.subject.trim()) {
      errors.subject = 'Subject is required';
    }
    
    if (!formData.message.trim()) {
      errors.message = 'Message is required';
    } else if (formData.message.trim().length < 20) {
      errors.message = 'Message must be at least 20 characters';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      try {
        setIsSubmitting(true);
        
        // Simulate API call with delay
        await new Promise(resolve => setTimeout(resolve, 1500));
        
        // In a real app, this would be an actual API call
        // const response = await fetch('/api/contact', {
        //   method: 'POST',
        //   headers: { 'Content-Type': 'application/json' },
        //   body: JSON.stringify(formData)
        // });
        
        // if (!response.ok) throw new Error('Failed to submit form');
        
        console.log('Form submitted:', formData);
        setSubmitSuccess(true);
        
        // Reset form on success
        setFormData({
          name: '',
          email: '',
          subject: '',
          message: '',
          reason: 'general'
        });
      } catch (error) {
        console.error('Error submitting form:', error);
        setSubmitError(true);
      } finally {
        setIsSubmitting(false);
      }
    }
  };
  
  const handleCloseSnackbar = () => {
    setSubmitSuccess(false);
    setSubmitError(false);
  };

  const contactReasons = [
    { value: 'general', label: 'General Inquiry' },
    { value: 'support', label: 'Technical Support' },
    { value: 'billing', label: 'Billing Question' },
    { value: 'feature', label: 'Feature Request' },
    { value: 'bug', label: 'Report a Bug' },
    { value: 'business', label: 'Business Opportunity' }
  ];

  const contactChannels = [
    {
      icon: <EmailIcon fontSize="large" color="primary" />,
      title: 'Email Us',
      description: 'For non-urgent inquiries',
      contact: 'support@1timemail.org',
      link: 'mailto:support@1timemail.org'
    },
    {
      icon: <HelpIcon fontSize="large" color="primary" />,
      title: 'Help Center',
      description: 'Browse our knowledge base',
      contact: 'help.1timemail.org',
      link: '/faq'
    },
    {
      icon: <BusinessIcon fontSize="large" color="primary" />,
      title: 'Business Inquiries',
      description: 'For partnerships and enterprise plans',
      contact: 'business@1timemail.org',
      link: 'mailto:business@1timemail.org'
    }
  ];

  return (
    <PageTemplate 
      title="Contact Us" 
      description="Have questions or need assistance? Get in touch with our team."
      breadcrumbs={[{ label: 'Contact', path: '/contact' }]}
    >
      <Grid container spacing={4}>
        <Grid item xs={12} md={5} lg={4}>
          <Card elevation={0} sx={{ borderRadius: 2, height: '100%', border: '1px solid', borderColor: 'divider' }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h5" fontWeight={600} gutterBottom>
                Get In Touch
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                Have a question or need help? Our team is ready to assist you.
              </Typography>
              
              <Stack spacing={3} sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <EmailIcon color="primary" />
                  <Box>
                    <Typography variant="subtitle1" fontWeight={600}>
                      Email Us
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      General inquiries: <br />
                      <a href="mailto:hello@1timemail.org" style={{ color: 'inherit', textDecoration: 'none' }}>hello@1timemail.org</a>
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Support: <br />
                      <a href="mailto:support@1timemail.org" style={{ color: 'inherit', textDecoration: 'none' }}>support@1timemail.org</a>
                    </Typography>
                  </Box>
                </Box>
                
                <Box>
                  <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                    Support Hours
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Monday - Friday: 9:00 AM - 6:00 PM PST
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Weekend: Email support only
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
                    We aim to respond to all inquiries within 24 hours.
                  </Typography>
                </Box>
                
                <Box>
                  <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                    Office Location
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    1TimeMail Inc.<br />
                    5932 Jones Way<br />
                    San Francisco, CA 94108<br />
                    United States
                  </Typography>
                </Box>
              </Stack>
              
              <Divider sx={{ my: 3 }} />
              
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                Follow Us
              </Typography>
              <Stack direction="row" spacing={1}>
                <Button 
                  variant="outlined" 
                  size="small"
                  href="https://twitter.com/1timemail"
                  target="_blank"
                  sx={{ minWidth: 'auto', px: 1.5 }}
                >
                  Twitter
                </Button>
                <Button 
                  variant="outlined" 
                  size="small"
                  href="https://github.com/1timemail"
                  target="_blank"
                  sx={{ minWidth: 'auto', px: 1.5 }}
                >
                  GitHub
                </Button>
                <Button 
                  variant="outlined" 
                  size="small"
                  href="https://linkedin.com/company/1timemail"
                  target="_blank"
                  sx={{ minWidth: 'auto', px: 1.5 }}
                >
                  LinkedIn
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={7}>
          <Paper 
            elevation={0} 
            sx={{ 
              p: 3, 
              border: '1px solid', 
              borderColor: 'divider',
              borderRadius: 3,
              mb: 4
            }}
          >
            <Typography variant="h5" gutterBottom>
              Send Us a Message
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Fill out the form below and we'll get back to you as soon as possible.
            </Typography>
            
            <Divider sx={{ my: 3 }} />
            
            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Your Name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    error={!!formErrors.name}
                    helperText={formErrors.name}
                    required
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Your Email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    error={!!formErrors.email}
                    helperText={formErrors.email}
                    required
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    select
                    fullWidth
                    label="Reason for Contact"
                    name="reason"
                    value={formData.reason}
                    onChange={handleChange}
                  >
                    {contactReasons.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    error={!!formErrors.subject}
                    helperText={formErrors.subject}
                    required
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Message"
                    name="message"
                    multiline
                    rows={6}
                    value={formData.message}
                    onChange={handleChange}
                    error={!!formErrors.message}
                    helperText={formErrors.message}
                    required
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <Button 
                    type="submit" 
                    variant="contained" 
                    color="primary" 
                    size="large"
                    fullWidth
                    sx={{ py: 1.5 }}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Paper>
          
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
            We typically respond within 1-2 business days. For urgent matters, please use our email support.
          </Typography>
        </Grid>
        
        <Grid item xs={12} md={5}>
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom>
              Contact Channels
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Choose the most convenient way to reach us.
            </Typography>
            
            <Stack spacing={3} sx={{ mt: 3 }}>
              {contactChannels.map((channel, index) => (
                <Card 
                  key={index} 
                  elevation={0}
                  sx={{ 
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: 'divider',
                    transition: 'transform 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                    }
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box sx={{ mr: 2 }}>
                        {channel.icon}
                      </Box>
                      <Box>
                        <Typography variant="h6" gutterBottom>
                          {channel.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          {channel.description}
                        </Typography>
                        <Button 
                          href={channel.link} 
                          variant="text" 
                          sx={{ fontWeight: 600, p: 0 }}
                        >
                          {channel.contact}
                        </Button>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          </Box>
          
          <Paper 
            elevation={0} 
            sx={{ 
              p: 3, 
              border: '1px solid', 
              borderColor: 'divider',
              borderRadius: 3,
              mt: 6
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <WarningIcon color="warning" sx={{ mr: 1 }} />
              <Typography variant="h6">
                Important Note
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" paragraph>
              Our support team cannot access or recover emails from expired temporary addresses due to our privacy policy. All temporary emails are permanently deleted after their lifespan.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              For Premium and Business users, please include your account email in your message for faster assistance.
            </Typography>
          </Paper>
        </Grid>
      </Grid>
      
      <Snackbar 
        open={submitSuccess} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity="success"
          variant="filled"
          icon={<CheckCircleIcon />}
        >
          Your message has been sent successfully! We'll get back to you soon.
        </Alert>
      </Snackbar>
      
      <Snackbar 
        open={submitError} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity="error"
          variant="filled"
        >
          There was an error sending your message. Please try again.
        </Alert>
      </Snackbar>
    </PageTemplate>
  );
};

export default ContactPage; 